import { CForm, CFormInput, CFormTextarea } from "@coreui/react";
import accomodationService from "@services/accomodation.service";
import { getToastStore } from "@utils/ToastStore";
import { useSpinner } from "@webkit/components/typescript/SpinnerContext";
import { ACCOMODATION_IMAGE_TYPE } from "@webkit/enums/accomodation-image-type.enum";
import { t } from 'i18next';
import { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { base64ToFile, errorToast, successToast } from "@webkit/functions/utils";
import { BaseAccomodation } from "@models/base-accomodation.model";
import { SaveAccomodation } from "@models/save-accomodation.model";

export default function EditAccomodation() {
    const navigate = useNavigate();
    const [title, setTitle] = useState('');
    const [id, setId] = useState(0);
    const [selectedLogo, setSelectedLogo] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [details, setDetails] = useState('');
    const [selectedCoverImageDesktop, setSelectedCoverImageDesktop] = useState<File | null>(null);
    const [coverDesktopPreview, setCoverDesktopPreview] = useState<string | null>(null);

    const [selectedCoverImageMobile, setSelectedCoverImageMobile] = useState<File | null>(null);
    const [coverMobilePreview, setCoverMobilePreview] = useState<string | null>(null);

    const isDisabled = (
        !title.trim() ||
        !coverMobilePreview ||
        !logoPreview ||
        !coverDesktopPreview

    );

    const ctxToast = getToastStore();

    const { showSpinner, hideSpinner } = useSpinner();

    const location = useLocation();
    const segments = location.pathname.split('/').filter(Boolean);

    useEffect(() => {
        recoverData();
    }, []);

    const recoverData = async () => {

        const accomodationId = segments.at(0) ?? '';
        const data = await accomodationService.findAccomodationById(accomodationId);
        const { accomodation } = data
        
        setDetails(data.detail)
        setId(accomodation.id)
        setTitle(accomodation.name);
        const base64StringLogo = accomodation.logo;
        const dataUrlLogo = `data:image/jpeg;base64,${base64StringLogo}`;
        setSelectedLogo(base64ToFile(dataUrlLogo, "logo.jpg"));
        setLogoPreview(dataUrlLogo);

        const base64StringImgMobile = accomodation.imgMobile;
        const dataUrlImgMobile = `data:image/jpeg;base64,${base64StringImgMobile}`;
        setSelectedCoverImageMobile(base64ToFile(dataUrlImgMobile, "imgMobile.jpg"));
        setCoverMobilePreview(dataUrlImgMobile);

        const base64StringImgDesktop = accomodation.imgDesktop;
        const dataUrlImgDesktop = `data:image/jpeg;base64,${base64StringImgDesktop}`;
        setSelectedCoverImageDesktop(base64ToFile(dataUrlImgDesktop, "imgDesktop.jpg"));
        setCoverDesktopPreview(dataUrlImgDesktop);

    };

    const handleSubmit = async () => {
        const formData = new FormData();
        showSpinner();
        if (selectedLogo) formData.append('logo', selectedLogo);
        if (selectedCoverImageDesktop) formData.append('imgDesktop', selectedCoverImageDesktop);
        if (selectedCoverImageMobile) formData.append('imgMobile', selectedCoverImageMobile);
        try {
            const baseAccomodation = new BaseAccomodation(title.trim());
            const saveAccomodation = {detail: details, accomodation: baseAccomodation}
            await accomodationService.updateAccomodation(id, formData, saveAccomodation);
            successToast(ctxToast, t("success.updated"));
            navigate(`/new-app/${id}`);
        } catch (error: any) {         
            errorToast(ctxToast, t("error.wrong_address"));
            return;
        } finally {
            hideSpinner();
        }

    }

    const handleFileChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        type: ACCOMODATION_IMAGE_TYPE
    ) => {
        const maxSize = 5 * 1024 * 1024;
        if (!e.target.files || e.target.files.length === 0) return;
        const file = e.target.files[0];

        if (file.type !== "image/jpeg" && file.type !== "image/png") { 
            errorToast(ctxToast, t("error.invalid_image_format"));
            e.target.value = "";
            return;
        }

        if (file.size > maxSize) {
          errorToast(ctxToast, t("error.too_large"));
          e.target.value = "";
          return;
        }

        switch (type) {
            case ACCOMODATION_IMAGE_TYPE.IMAGE_LOGO:
                setSelectedLogo(file);
                setLogoPreview(URL.createObjectURL(file));
                break;
            case ACCOMODATION_IMAGE_TYPE.IMAGE_COVER_DESKTOP:
                setSelectedCoverImageDesktop(file);
                setCoverDesktopPreview(URL.createObjectURL(file));
                break;
            case ACCOMODATION_IMAGE_TYPE.IMAGE_COVER_MOBILE:
                setSelectedCoverImageMobile(file);
                setCoverMobilePreview(URL.createObjectURL(file));
                break;
            default:
                break;
        }
    };

    const handleRemoveTopBox = (idx: number, e: React.MouseEvent) => {
        e.stopPropagation();
        switch (idx) {
            case 0:
                setSelectedLogo(null);
                setLogoPreview(null);
                break;
            case 1:
                setSelectedCoverImageDesktop(null);
                setCoverDesktopPreview(null);
                break;
            case 2:
                setSelectedCoverImageMobile(null);
                setCoverMobilePreview(null);
                break;
        }
        // reset input file
        const inputEl = document.getElementById(`fileInput-${idx}`) as HTMLInputElement;
        if (inputEl) inputEl.value = "";
    };

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div className="card shadow-lg" style={{ width: '70%', borderRadius: '25px' }}>
                <div className="hst-card-header card-header text-uppercase">
                    {t('label.create_hostyble')}
                </div>
                <div className="card-body">
                    <CForm className="p-5">
                        <CFormInput
                            type="text"
                            label={`${t('label.title')} ${t('placeholder.obligatory')}`}
                            className="mb-3"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            required
                        />
                        <CFormTextarea
                            label={`${t("label.details")} ${t('placeholder.obligatory')}`}
                            rows={5}
                            className="mb-4"
                            value={details}
                            onChange={(e) => setDetails(e.target.value)}
                        />
                        <h3 style={{ marginTop: "50px", color: "#3B82F6" }} className="mb-3">{t("label.album")}</h3>
                        <p >{t("label.img_type")}</p>
                        <p >{t("label.images_suggestion")}</p>
                        <div
                            className="mb-3 img-container"
                        >
                            {[
                                { file: selectedLogo, preview: logoPreview, label: t('label.logo_upload'), onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleFileChange(e, ACCOMODATION_IMAGE_TYPE.IMAGE_LOGO) },
                                { file: selectedCoverImageDesktop, preview: coverDesktopPreview, label: t('label.cover_upload_web'), onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleFileChange(e, ACCOMODATION_IMAGE_TYPE.IMAGE_COVER_DESKTOP) },
                                { file: selectedCoverImageMobile, preview: coverMobilePreview, label: t('label.cover_upload_mobile'), onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleFileChange(e, ACCOMODATION_IMAGE_TYPE.IMAGE_COVER_MOBILE) },
                            ].map((item, idx) => (
                                <div key={idx} className="w-100">
                                    <div
                                        style={{
                                            border: "2px dashed #ccc",
                                            borderRadius: "10px",
                                            height: "150px",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: "pointer",
                                            background: "#fafafa",
                                            position: "relative"
                                        }}
                                        onClick={() => document.getElementById(`fileInput-${idx}`)?.click()}
                                    >
                                        {item.preview ? (<>
                                            <img
                                                src={item.preview}
                                                alt={`preview-${idx}`}
                                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                            />
                                            {/* Bottone close */}
                                            <button
                                                className="hst-close-button-box"
                                                type="button"
                                                onClick={(e) => handleRemoveTopBox(idx, e)}
                                            >
                                                ✕
                                            </button>
                                        </>
                                        ) : (
                                            <span style={{ fontSize: "2rem", color: "#aaa" }}>+</span>
                                        )}
                                        <input
                                            type="file"
                                            accept=".jpg, .jpeg, .png"
                                            id={`fileInput-${idx}`}
                                            style={{ display: "none" }}
                                            onChange={item.onChange}
                                            required
                                        />

                                    </div>
                                    <div style={{ textAlign: "center", width: "100%" }}>
                                        {item.label} {t('placeholder.obligatory')}
                                    </div>
                                </div>
                            ))}
                        </div>

                    </CForm>
                </div>
                <div
                    className="card-footer bg-transparent"
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                    <Button
                        className="hst-btn-secondary"
                        onClick={() => navigate(-1)}
                    >
                        {t('text.go_back')}
                    </Button>

                    <Button
                        className="text-uppercase hst-btn-primary"
                        onClick={handleSubmit}
                        style={{
                            borderRadius: '25px',
                            width: '20%',
                            backgroundColor: '#00BFA6',
                            border: 'none',
                            fontWeight:"500"
                        }}
                        disabled={isDisabled}
                    >
                        {t('button.save_changes')}
                    </Button>
                </div>
            </div>
        </div>
    );
}